import React, {useEffect, useRef, useState} from "react";
import Heading from "../../../../components/dashboard/heading-component/Heading";
import {CalendarIcon, CompleteVideoIcon} from "../../../../../assets/icons/defaultIcons";
import {Col, Row} from "react-bootstrap";
import ReactPlayer from "react-player";
import VaultCard from "../../../../components/dashboard/cards/vault-card/VaultCard";
import "./vault-pages.scss";
import {useNavigate, useParams} from "react-router-dom";
import {VaultService} from "../../../../services/vault-api.service";
import dayjs from "dayjs";
import VaultDetailSkeleton from "../../../skeletons/vault/vault-pages/VaultDetailSkeleton";
import {GoFile} from "react-icons/go";
import NoRecord from "../../../../components/dashboard/no-record/NoRecord";
import {Button} from "antd";

// Add custom CSS to prevent video downloads
const preventDownloadStyles = `
    /* Apply this style to the vault-pages.scss file for a permanent solution */
    .player video::-internal-media-controls-download-button {
        display: none !important;
    }
    
    .player video::-webkit-media-controls-enclosure {
        overflow: hidden !important;
    }
    
    .player video::-webkit-media-controls-panel {
        width: calc(100% + 30px) !important;
    }
    
    /* Disable text selection on the player */
    .player {
        user-select: none;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
    }
`;

export default function VaultDetails() {
    const [playing, setPlaying] = useState(true); // Start with playing=true to autoplay when ready
    const playerRef = useRef(null);
    const watchedTimeRef = useRef(0); // Store watched time
    const [data, setData] = useState(null);
    const [type, setType] = useState(null);
    const [moreData, setMoreData] = useState([]);
    const [page, setPage] = useState(1);
    const [lastPageReached, setLastPageReached] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loader, setLoader] = useState(false);
    const [videoEnded, setVideoEnded] = useState(false); // Track if video has ended
    const limit = 12;
    let { id } = useParams();
    const navigate = useNavigate();

    const handlePlayPause = () => {
        setPlaying(!playing);
    };

    // Function to convert seconds to "MM:SS" format
    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
    };

    // Function to send watched time to API
    const sendWatchTime = async () => {
        const watchedAtFormatted = formatTime(watchedTimeRef.current);

        if (watchedTimeRef.current > 0) {
            try {
              const payload = {
                  vaultId: id,
                  watchedAt: watchedAtFormatted,
              }
                VaultService.TrackVideoProgress(payload)

            } catch (error) {
                console.error("Error sending watch time:", error);
            }
        }
    };

    // Track video progress
    const handleProgress = (progress) => {
        watchedTimeRef.current = progress.playedSeconds;
    };

    // Handle pause event
    const handlePause = () => {
        sendWatchTime();
    };

    // Handle video end event
    const handleVideoEnd = () => {
        setVideoEnded(true);
        sendWatchTime();
        // Mark the video as fully watched
        try {
            const watchedAtFormatted = data?.length || "00:00"; // Use video length as watch time
            const payload = {
                vaultId: id,
                watchedAt: watchedAtFormatted,
            };
            VaultService.TrackVideoProgress(payload);
        } catch (error) {
            console.error("Error sending complete watch time:", error);
        }
    };

    // Restart the video
    const handleRestartVideo = () => {
        setVideoEnded(false);
        if (playerRef.current) {
            playerRef.current.seekTo(0);
            setPlaying(true);
        }
    };

    // Handle navigation away or tab close
    useEffect(() => {
        const handleUnload = () => sendWatchTime();
        window.addEventListener("beforeunload", handleUnload);

        return () => {
            sendWatchTime(); // Send on unmount
            window.removeEventListener("beforeunload", handleUnload);
        };
    }, []);

    // Fetch video data
    function fetchData(id) {
        setLoader(true);
        setVideoEnded(false); // Reset video ended state when loading new video
        setPlaying(true); // Ensure auto-play for new video
        VaultService.getSingleVault(id)
            .then((singleVaultResponse) => {
                if (singleVaultResponse?.data?.data?.status) {
                    setData(singleVaultResponse.data.data.data.result);
                    const videosType = singleVaultResponse.data.data.data.result.type;
                    setType(videosType);
                    return videosType ? VaultService.getVault(videosType, page, limit, id) : null;
                }
                return null;
            })
            .then((vaultDataResponse) => {
                if (vaultDataResponse && vaultDataResponse.data.data.status) {
                    setMoreData(vaultDataResponse.data.data.data.result);
                    setLastPageReached(!vaultDataResponse.data.data.data.pagination.hasNext);
                }
            })
            .catch((error) => console.error("Error fetching data:", error))
            .finally(() => setLoader(false));
    }

    const loadMore = () => {
        if (lastPageReached) return;
        setLoadingMore(true);
        VaultService.getVault(type, page + 1, limit, id)
            .then((vaultDataResponse) => {
                if (vaultDataResponse && vaultDataResponse.data.data.status) {
                    const newVideos = vaultDataResponse.data.data.data.result;
                    setMoreData((prevData) => [...prevData, ...newVideos]);
                    setPage((prevPage) => prevPage + 1);
                    setLastPageReached(!vaultDataResponse.data.data.data.pagination.hasNext);
                }
            })
            .catch((error) => console.error("Error loading more videos:", error))
            .finally(() => setLoadingMore(false));
    };

    useEffect(() => {
        fetchData(id);
    }, [id]);

    if (loader) {
        return <VaultDetailSkeleton />;
    }    return (
        <div className={"vault-details"}>
            {/* Apply custom styles to hide download button */}
            <style>{preventDownloadStyles}</style>
            <Row>
                <Col md={12}>
                    <Heading back={true} heading={"Vault Detail"} />
                    <div className={"vault-details-section"}>
                        <div className={"left-sec"}>                            {data && (
                                <div className={"video-section"}>
                                    <div 
                                        className={"player"} 
                                        style={{ width: "100%", height: "600px", position: "relative" }}
                                        onContextMenu={(e) => e.preventDefault()} // Prevent right-click menu
                                    >
                                        {videoEnded && (
                                            <div className="video-completion-overlay">
                                                <div className="video-completion-message">
                                                    <div className="completion-icon">
                                                        <CompleteVideoIcon/>
                                                    </div>
                                                    <h3>Video Completed</h3>
                                                    <p>You have successfully completed this video!</p>
                                                    <button className="restart-button" onClick={handleRestartVideo}>
                                                        Watch Again
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        <ReactPlayer
                                            ref={playerRef}
                                            url={data.url}
                                            controls={true}
                                            height={"100%"}
                                            playing={playing}
                                            width={"100%"}                                            
                                            light={data?.thumbnail}
                                            onProgress={handleProgress} // Track progress
                                            onPause={handlePause} // Send API on pause
                                            onEnded={handleVideoEnd} // Handle video end
                                            config={{
                                                file: {
                                                    attributes: {
                                                        controlsList: 'nodownload', // Disable download option
                                                        // Remove disablePictureInPicture to enable PiP
                                                    },
                                                },
                                                youtube: {
                                                    playerVars: {
                                                        showinfo: 0,
                                                        disablekb: 1,
                                                        fs: 1, // Enable fullscreen button
                                                        controls: 1,
                                                        modestbranding: 1,
                                                        rel: 0, // Don't show related videos
                                                        download: 0 // Prevent downloading
                                                    }
                                                },
                                                vimeo: {
                                                    playerOptions: {
                                                        controls: true,
                                                        pip: true, // Enable picture-in-picture
                                                        download: false // Disable download
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                    <h5>
                                        <CalendarIcon /> {dayjs(data?.createdAt).format("MM-DD-YYYY")}
                                    </h5>
                                    <h3>{data?.name}</h3>
                                    <p>{data?.description}</p>

                                    {data && Array.isArray(data.attachments) && data.attachments.length > 0 && (
                                        <div className={"attachments"}>
                                            <h3>Attachments</h3>
                                            <div className={"attach-sec"}>
                                                {data.attachments.map((item, index) => (
                                                    <div key={index} className={"attachment-box"} onClick={() => window.open(item.url, "_blank", "noopener noreferrer")}>
                                                        <div className={"icon"}>
                                                            <GoFile />
                                                        </div>
                                                        <h4>{item.name}</h4>
                                                        <p>{item.size}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className={"right-sec"}>
                            <div className={"more-videos"}>
                                <h4 className={"more-head"}>More Videos</h4>
                                {moreData.length > 0 ? (
                                    <div>
                                        <div className={"more-video-sec"}>
                                            {moreData.map((item, index) => (
                                                <VaultCard key={index} img={item.thumbnail} title={item.name} date={dayjs(item?.createdAt).format("MM-DD-YYYY")} id={item._id} videotime={item.length} />
                                            ))}
                                        </div>
                                        {!lastPageReached && (
                                            <Button type="link" className={"load-more-btn"} loading={loadingMore} onClick={loadMore}>
                                                Load More
                                            </Button>
                                        )}
                                    </div>
                                ) : (
                                    <NoRecord />
                                )}
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    );
}
